import {
	ActionButton,
	ExportDropdown,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	PeriodPanel,
	SectionCard,
	useToast,
	EmptyState,
} from '@practium/ui';
import type { CustomPanelControls, SectionTab } from '@practium/ui';
import { useCallback, useEffect, useState } from 'react';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { AnalysesPageSkeleton } from '../components/Skeletons/AnalysesPageSkeleton';
import { SoilAnalysisTable } from '../components/Tables/SoilAnalysisTable/SoilAnalysisTable';
import { PlantAnalysisTable } from '../components/Tables/PlantAnalysisTable/PlantAnalysisTable';
import type { UseAnalysesPageProps } from '../hooks/useAnalysesPage';
import { useAnalysesPage } from '../hooks/useAnalysesPage';
import type { AnalysesTab } from '../types/analyses.types';
import styles from './AnalysesPage.module.css';

export interface AnalysesPageProps extends UseAnalysesPageProps {
	onAddNew?: (tab: AnalysesTab) => void;
	onViewItem?: (tab: AnalysesTab, id: string) => void;
	onReportClick?: () => void;
}

export const AnalysesPage = ({
	onAddNew,
	onViewItem,
	onReportClick,
	...hookProps
}: AnalysesPageProps) => {
	const {
		activeTab,
		setActiveTab,
		filteredSoil,
		filteredPlant,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		filterGroups,
		page,
		setPage,
		totalPages,
		isTableLoading,
		isTableFetching,
		isTableError,
		refetchTable,
		isChartsLoading,
		isChartsFetching,
		isChartsError,
		exportSoilAnalysis,
		isExportingSoilAnalysis,
		permissions,
		analysesPieData,
		analysesLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		analysesFooterText,
	} = useAnalysesPage(hookProps);
	const toast = useToast();
	const [hasRenderedContent, setHasRenderedContent] = useState(false);
	const isInitialLoading = !hasRenderedContent && isTableLoading && isChartsLoading;

	useEffect(() => {
		if (!isInitialLoading) setHasRenderedContent(true);
	}, [isInitialLoading]);

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					setCustomDateFrom(from);
					setCustomDateTo(to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, setCustomDateFrom, setCustomDateTo]
	);

	if (isInitialLoading) return <AnalysesPageSkeleton />;

	const tableStatus = isTableError ? (
		<div role="alert">
			<EmptyState
				title="Não foi possível carregar as análises"
				description="Tente novamente para carregar os dados."
			/>
			<ActionButton text="Tentar novamente" onClick={() => void refetchTable()} />
		</div>
	) : null;

	const tableContent =
		tableStatus ??
		(activeTab === 'soil' ? (
			<SoilAnalysisTable
				analyses={filteredSoil}
				onRowClick={onViewItem ? (id) => onViewItem('soil', id) : undefined}
			/>
		) : (
			<PlantAnalysisTable
				analyses={filteredPlant}
				onRowClick={onViewItem ? (id) => onViewItem('plant', id) : undefined}
			/>
		));
	const tabs: SectionTab<AnalysesTab>[] = [
		{ key: 'soil', label: 'Solo', content: tableContent },
		{ key: 'plant', label: 'Foliar', content: tableContent },
	];

	const handleReport = () => onReportClick?.();
	const handleExportSoil = (format: 'pdf' | 'xlsx') => {
		void exportSoilAnalysis(format).catch(() =>
			toast.error('Não foi possível exportar as análises de solo.')
		);
	};
	const chartEmptyMessage = isChartsError ? 'Não foi possível carregar as estatísticas' : undefined;

	return (
		<ListPageLayout
			sidebarWidth="26rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts>
					<ListPageChartCard isFetching={isChartsFetching && !isChartsLoading && !isChartsError}>
						<PiePercentageChart
							title="Distribuição de Análises"
							subtitle="Distribuição entre análises de solo e foliares"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={isChartsError ? [] : analysesPieData}
							isLoading={isChartsLoading}
							emptyMessage={chartEmptyMessage}
						/>
					</ListPageChartCard>
					<ListPageChartCard isFetching={isChartsFetching && !isChartsLoading && !isChartsError}>
						<ComparisonLineChart
							title="Análises por Período"
							mainValue={chartMainValue}
							trendValue={chartTrendValue}
							trendLabel="vs semana anterior"
							trendDirection={chartTrendDirection}
							footerText={analysesFooterText}
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={isChartsError ? [] : analysesLineData}
							isLoading={isChartsLoading}
							emptyMessage={chartEmptyMessage}
							xAxisKey="day"
							line1Key="atual"
							line1Label="Semana atual"
							line2Key="anterior"
							line2Label="Semana anterior"
							tooltipTitle="Análises"
						/>
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<div className={styles.tableSection}>
				<SectionCard
					title="Gestão de Análises"
					subtitle="Gerencie análises de solo e foliares cadastradas no sistema"
					tabs={tabs}
					isLoading={isTableLoading && !isTableError}
					isFetching={isTableFetching && !isTableError}
					loadingLabel="Carregando análises"
					activeTab={activeTab}
					onTabChange={setActiveTab}
					search={true}
					searchQuery={searchQuery}
					onSearch={setSearchQuery}
					sortAsc={sortAsc}
					onToggleSort={toggleSort}
					filterSlot={
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
							customPanels={{ 'periodo:custom': renderPeriodPanel }}
						/>
					}
					footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
					headerAction={
						<>
							{permissions.canCreate && (
								<ActionButton text="Adicionar" onClick={() => onAddNew?.(activeTab)} />
							)}
							{activeTab === 'soil' && (
								<ExportDropdown
									disabled={isExportingSoilAnalysis}
									label={isExportingSoilAnalysis ? 'Exportando…' : 'Exportar'}
									onExportExcel={() => handleExportSoil('xlsx')}
									onExportPdf={() => handleExportSoil('pdf')}
								/>
							)}
							<ActionButton text="Ver Relatório" onClick={handleReport} />
						</>
					}
				/>
			</div>
		</ListPageLayout>
	);
};
